import { Typography } from '@mui/material'
import { Fragment } from 'react'

import { Citation } from '@/apps/latest/components/atoms/Citation'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { type KnowledgeCitation } from '@/features/internal/knowledge'
import { scaledFontSize } from '@/shared/styles/typography'

import { splitByCitations } from './answerSegments'

// citation.md: 該当行を 600ms ハイライトして fade-out
const HIGHLIGHT_MS = 600

interface Props {
  content: string
  citations: KnowledgeCitation[]
  // 出典行の DOM id を組み立てる。同一画面に複数往復が並ぶため message 単位で名前空間を切る。
  citationElementId: (n: number) => string
}

/**
 * 回答本文。`[N]` を Citation バッジに置き換え、click で下の出典行へ飛ばす。
 *
 * どの一文がどの資料由来かが読めないと、利用者が原典に当たれず結局本部への
 * 問い合わせが残る。バッジと出典行の対応が本機能の主な価値になる。
 */
export const AnswerBody = ({
  content,
  citations,
  citationElementId,
}: Props) => {
  const segments = splitByCitations(
    content,
    citations.map((c) => c.n)
  )

  const jump = (n: number) => {
    const target = document.getElementById(citationElementId(n))
    if (!target) return
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    target.style.backgroundColor = colorTokens.state.warnBg
    window.setTimeout(() => {
      target.style.backgroundColor = ''
    }, HIGHLIGHT_MS)
  }

  return (
    <Typography
      component="div"
      sx={{
        // 読む本文は文字サイズモードに追従する (ADR-0019)
        fontSize: scaledFontSize('bodySm'),
        lineHeight: 1.6,
        color: colorTokens.fg.default,
        whiteSpace: 'pre-wrap',
      }}
    >
      {segments.map((segment, index) =>
        segment.kind === 'text' ? (
          <Fragment key={index}>{segment.text}</Fragment>
        ) : (
          <Citation
            key={index}
            index={segment.n}
            onJump={jump}
            aria-label={`出典 ${segment.n} へ移動`}
          />
        )
      )}
    </Typography>
  )
}
